import React from 'react';
import PropTypes from 'prop-types';

import { request, call } from './utils';

const Context = React.createContext({
  username: null,
  authorization: null,
});

/**
 * **Access:**
 * ```js
 * import {SessionInfo} from '@iss/client-react-components'
 * ```
 * Privides session info as context in the component hierarchy.
 * This sets up the username and authorization token used to make API calls.
 *
 * @class SessionInfo
 * @component
 */
export default class SessionInfo extends React.PureComponent {
  static propTypes = {
    /** The name of the currently logged in user */
    username: PropTypes.string,
    /** The auth token string to pass in the x-iss-authtoken header */
    authorization: PropTypes.string,
    /** A callback to recieve notification of a request failure. This is a hook point to implement listening for 401 and 403 */
    onRequestFailure: PropTypes.func,
    /** A callback to recieve notification when the auth token is refreshed in a response */
    onUpdatedToken: PropTypes.func,
  };

  static Context = Context;

  componentDidMount() {
    this.unsubscribe = request.subscribe((type, ...data) => this.notification(type, ...data));
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  notification(type, ...data) {
    switch (type) {
      case 'failure': {
        const [response] = data;
        call(this.props.onRequestFailure, response);
        break;
      }

      case 'updated-token': {
        const [token] = data;
        call(this.props.onUpdatedToken, token);
        break;
      }

      default: {
        console.warn('Unhandled request notification type: ', type);
      }
    }
  }

  render() {
    const { username, authorization, ...props } = this.props;
    request.setAuthorization(authorization);
    request.username = username;

    return <Context.Provider value={{ authorization, username }} {...props} />;
  }
}
